import { useAuthStoreUser } from "@/stores/app/authUser";
import { IdUsuario } from "../common-types";
import { ResponseConta } from "../contas/contas.service";
import { GenericService } from "../generic-service";
import { ResponseInbox } from "../inbox/inbox.service";
import type { SoundPreferences } from "@/stores/app/soundStore";

export interface CreateUsuario {
  nome: string;
  email: string;
  senha: string;
  inboxes?: string[];
  canais?: string[];
  contas?: string[];
  refreshToken?: string;
  foto?: string;
  root?: boolean;
  doisFatores?: boolean;
  notificacaoNavegador?: boolean;
  tema?: 'light' | 'dark';
  som?: SoundPreferences;
}

export type UpdateUsuario = Partial<CreateUsuario> & { id: IdUsuario }

export interface ResponseUsuario {
  id: IdUsuario;
  email: string;
  nome: string;
  dataCriacao: Date;
  contas?: ResponseConta[];
  inboxes?: ResponseInbox[];
  contasDisplay?: string;
  foto?: string;
  isOnline?: boolean;
  globalAccess?: boolean;
  doisFatores?: boolean;
  notificacaoNavegador?: boolean;
  tema?: 'light' | 'dark';
  som?: SoundPreferences;
}

export class UsuariosService extends GenericService<CreateUsuario, UpdateUsuario, ResponseUsuario> {
  constructor() { super('usuarios') }

  async getContasVinculadas(idUsuario: string): Promise<ResponseConta[]> {
    return await this.f.get(`/${idUsuario}/contas`) as ResponseConta[];
  }

  async getByInbox(idInbox: string): Promise<ResponseUsuario[]> {
    return await this.f.get(`/inbox/${idInbox}`) as ResponseUsuario[]
  }

  async getByConta(idConta: string): Promise<ResponseUsuario[]> {
    return await this.f.get(`/conta/${idConta}`) as ResponseUsuario[];
  }

  /**
   * "Excluir" atendente pela área do cliente: o servidor NÃO desativa o usuário — só o
   * tira da conta informada e dos inboxes dela (T-10). O mesmo DELETE feito pelo painel
   * administrativo continua desativando.
   */
  async desvincularDaConta(idUsuario: string, idConta: string): Promise<void> {
    await this.f.delete(`/${idUsuario}?idConta=${encodeURIComponent(idConta)}`);
  }

  async verificarEmail(email: string): Promise<{ existe: boolean }> {
    return await this.f.get('/verificar-email', {email}) as { existe:boolean };
  }

  async regenerateRefreshToken(idUsuario: string) {
    return await this.f.patch(`/regenerate-refresh-token/${idUsuario}`);
  }

  async forceLogout(usuarioId: string) {
    const authStore = useAuthStoreUser();

    if (!authStore.wsService)
      throw new Error('[usuariosService] [forceLogout] authStore.wsService was null.');

    const idConta = localStorage.getItem('selectedContaId');
    if (!idConta)
      throw new Error('[usuariosService] [forceLogout] selectedContaId não encontrado no localStorage.');

    // Emite o WS
    void authStore.wsService.emitSystem('FORCE_LOGOUT', {
      usuarioId,
      motivo: 'Admin realizou logout forçado.'
    });

    // Registra auditoria
    await this.f.post(`/${usuarioId}/force-logout-audit`, { idConta });
  }
}
